import React, { useState, useCallback } from 'react';
import { Text, Stack, Cluster, Button, IconButton, Icon, List, Switch } from '@iqueue/iq-ui';
import Modal from '../ui/Modal';
import Field from '../ui/Field';
import Select from '../ui/Select';
import Section, { Empty } from './Section';
import { useConfirm } from '../ui/ConfirmDialog';
import { useAdminData, useSlowWait } from './adminData';
import { api } from '../../lib/api';
import { formatElapsed, runStateMeta } from '../../lib/formatters';
import { agentIcon } from '../../lib/agentRegistry';
import AgentForm from './AgentForm';
import UsagePanel from './UsagePanel';

/** Сколько последних строк лога показываем: диалог не резиновый, а важен хвост. */
const LOG_TAIL = 200;

/**
 * Кусок диагностики: подпись и текст, разложенный по строкам. `\n` внутри одного
 * Text схлопнулся бы в пробел (переносы делает CSS, которого у нас нет), поэтому
 * строка лога = строка разметки — единственный честный способ без своих стилей.
 */
function LogBlock({ label, text }) {
  const lines = String(text || '').replace(/\s+$/, '').split('\n');
  const shown = lines.slice(-LOG_TAIL);
  const cut = lines.length - shown.length;
  return (
    <Stack gap={50}>
      <Cluster gap={100} align="baseline">
        <Text variant="label-small" muted render={<span />}>{label}</Text>
        {cut > 0 && <Text variant="label-small" muted render={<span />}>· последние {LOG_TAIL} из {lines.length} строк</Text>}
      </Cluster>
      {text ? (
        <Stack gap={0}>
          {shown.map((line, i) => <Text key={i} variant="body-small" render={<span />}>{line || ' '}</Text>)}
        </Stack>
      ) : <Text variant="body-small" muted>(пусто)</Text>}
    </Stack>
  );
}

/**
 * Admin: agents CRUD, runs (real PIDs), permission rules, contexts, users.
 *
 * Экран собран по рецепту «Settings screen»: секциями, каждая — своя подпись плюс
 * свой `Box surface="ground" blockRoot` (см. ./Section). Ни одной Card, ни одного
 * Divider, ни одного padding поверх блока — отступы приходят из дома.
 *
 * Действия строк: `trailingAction` рендерится СОСЕДОМ основного действия строки и
 * молча пропадает, если основного действия нет (List.js: `trailingAction != null &&
 * (onClick || href)`). Поэтому каждая строка с actions либо получила своё onClick,
 * либо отдаёт контрол в `trailing` — он законен ровно на строке БЕЗ основного
 * действия (иначе контрол внутри контрола и двойной клик).
 *
 * Ошибки разведены по природе: разовая (нажали кнопку — не вышло) уходит в тост
 * приложения через `onError`, а сорванный опрос живёт секцией наверху и исчезает
 * сам на следующем удачном круге. Бесхозной строки текста над блоками нет.
 */
export default function AdminPanel({ onAgentsChanged, onError }) {
  const [editAgent, setEditAgent] = useState(undefined);
  const [runLog, setRunLog] = useState(null);
  const [ctxForm, setCtxForm] = useState(null);

  // Снапшот переживает уход со вкладки, поэтому повторный вход рисуется сразу и
  // целиком (см. ./adminData). Пусто здесь означает ровно один случай: самый
  // первый заход, когда сервер ещё не ответил.
  const [confirmDialog, confirm] = useConfirm();
  const { data, stale, refresh: reload } = useAdminData();
  const waiting = useSlowWait(!data);

  // Разовая ошибка действия — в тост приложения; молча глотать её нельзя, а
  // рисовать поверх экрана нечем (Snackbar заявлен в библиотеку, §10.3).
  const fail = useCallback((e) => {
    if (onError) onError(e.message, 'error');
  }, [onError]);

  const saveAgent = useCallback(async (body, id) => {
    if (id) await api.adminUpdateAgent(id, body);
    else await api.adminCreateAgent(body);
    await reload();
    if (onAgentsChanged) onAgentsChanged();
  }, [reload, onAgentsChanged]);

  const toggleAgent = useCallback(async (a, on) => {
    try {
      if (on) await api.adminEnableAgent(a.id); else await api.adminDisableAgent(a.id);
      await reload();
      if (onAgentsChanged) onAgentsChanged();
    } catch (e) { fail(e); }
  }, [reload, onAgentsChanged, fail]);

  const { agents = [], profiles = [], runnerTypes = ['claude'], models = [],
    runs = [], rules = [], contexts = [], pendingUsers = [] } = data || {};
  const activeRuns = runs.filter(r => ['starting', 'running'].includes(r.state));
  const doneRuns = runs.filter(r => !['starting', 'running'].includes(r.state)).slice(0, 15);
  // Живые сверху, за ними последние завершённые: одна лента процессов.
  const shownRuns = [...activeRuns, ...doneRuns];
  const activeRules = rules.filter(r => r.active);

  // Основное действие строки процесса — открыть диагностику; отмена живёт рядом с ним.
  // Глиф слева — исход процесса (галочка / ошибка / отмена / таймаут), а не
  // квадратик «stop», который читался как чекбокс и ничего не сообщал.
  const runItem = (r) => {
    const live = ['starting', 'running'].includes(r.state);
    const meta = runStateMeta(r.state);
    return (
      <List.Item
        key={r.id}
        leading={<Icon name={meta.icon} />}
        supporting={`pid ${r.pid || '—'} · ${meta.label} · ${r.startedAt ? formatElapsed(r.startedAt, r.finishedAt) : ''}`}
        onClick={async () => {
          try { setRunLog(await api.adminRunLog(r.id)); } catch (e) { fail(e); }
        }}
        trailingAction={live
          ? (
            <IconButton
              icon="stop_circle" size="xs" tone="error" label="Отменить"
              onClick={async () => { try { await api.cancelRun(r.id); reload(); } catch (e) { fail(e); } }}
            />
          )
          : undefined}
      >
        @{r.agentId}
      </List.Item>
    );
  };

  // Первый заход: до порога не показываем ничего — на локальном сервере ответ
  // успевает прийти, и промежуточного кадра не существует вовсе. Каркас
  // появляется, только если ожидание стало настоящим.
  if (!data) return waiting ? <Empty>Загружаю админку…</Empty> : null;

  return (
    <Stack gap={400}>
      {confirmDialog}
      {stale && (
        <Section>
          <Empty>Данные устарели: {stale}</Empty>
        </Section>
      )}

      <UsagePanel />

      {pendingUsers.length > 0 && (
        <Section>
          <List aria-label="Ожидают активации">
            {pendingUsers.map(u => (
              <List.Item
                key={u.id}
                leading={<Icon name="person_add" />}
                // Строка без основного действия — контрол в `trailing` владеет
                // нажатием один, как в строке настроек.
                trailing={<Button size="xs" variant="tonal" onClick={async () => { try { await api.adminActivate(u.id); reload(); } catch (e) { fail(e); } }}>Активировать</Button>}
              >
                {u.username}
              </List.Item>
            ))}
          </List>
        </Section>
      )}

      <Section
        action={<Button size="xs" variant="text" startIcon={<Icon name="add" />} onClick={() => setEditAgent(null)}>Создать агента</Button>}
      >
        {agents.length === 0 ? (
          <Empty action={<Button variant="text" onClick={() => setEditAgent(null)}>Создать агента</Button>}>
            Агентов пока нет — некому отвечать в комнатах.
          </Empty>
        ) : (
          <List aria-label="Агенты">
            {agents.map(a => (
              <List.Item
                key={a.id}
                leading={<Icon name={agentIcon(a.avatar)} />}
                supporting={`${a.runner_type} · ${a.permission_profile} · ${a.model || 'модель по умолчанию'}`}
                onClick={() => setEditAgent(a)}
                // Выключатель — в `trailingAction`, а не в `trailing`: у строки есть
                // основное действие (открыть форму), и контрол в `trailing` был бы
                // вложен в него — один клик отработал бы дважды.
                trailingAction={(
                  <Switch
                    checked={!!a.active}
                    onCheckedChange={(v) => toggleAgent(a, Boolean(v))}
                    aria-label={`@${a.handle} включён`}
                  />
                )}
              >
                @{a.handle} · {a.name}
              </List.Item>
            ))}
          </List>
        )}
      </Section>

      {/* Активные и завершённые — теперь один блок: без подписей два подряд
          идущих блока процессов были неразличимы, а состояние каждого и так
          написано в самой строке (глиф + «выполняется/завершён»). */}
      <Section>
        {shownRuns.length === 0 ? <Empty>Процессов пока не было</Empty> : (
          <List aria-label="Процессы">{shownRuns.map(runItem)}</List>
        )}
      </Section>

      <Section>
        {activeRules.length === 0 ? <Empty>Нет активных правил</Empty> : (
          <List aria-label="Permission rules">
            {activeRules.map(r => (
              <List.Item
                key={r.id}
                leading={<Icon name="rule" />}
                supporting={`${r.action_pattern} / ${r.target_pattern}`}
                trailing={(
                  <IconButton
                    icon="delete" size="xs" tone="error" label="Отозвать"
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Отозвать правило для @${r.agent_id}?`,
                        description: `Разрешение «${r.action_pattern} / ${r.target_pattern}» перестанет действовать — агент снова будет спрашивать подтверждение.`,
                        confirmLabel: 'Отозвать',
                        icon: 'rule',
                      });
                      if (!ok) return;
                      try { await api.adminRevokeRule(r.id); reload(); } catch (e) { fail(e); }
                    }}
                  />
                )}
              >
                <Cluster gap={50} align="center" wrap={false}>
                  {r.user_id}
                  <Icon name="arrow_forward" size="s" />
                  @{r.agent_id}
                </Cluster>
              </List.Item>
            ))}
          </List>
        )}
      </Section>

      <Section
        action={<Button size="xs" variant="text" startIcon={<Icon name="add" />} onClick={() => setCtxForm({ type: 'git_repository', name: '', path: '', baseBranch: '' })}>Добавить контекст</Button>}
      >
        {contexts.length === 0 ? (
          <Empty action={<Button variant="text" onClick={() => setCtxForm({ type: 'git_repository', name: '', path: '', baseBranch: '' })}>Добавить контекст</Button>}>
            Контекстов нет — агенты работают без репозитория и каталога.
          </Empty>
        ) : (
          <List aria-label="Execution contexts">
            {contexts.map(c => (
              <List.Item key={c.id} leading={<Icon name="folder" />} supporting={`${c.type} · ${c.config.repoPath || c.config.path || ''}`}>
                {c.name}
              </List.Item>
            ))}
          </List>
        )}
      </Section>

      {editAgent !== undefined && (
        <AgentForm
          agent={editAgent}
          profiles={profiles}
          runnerTypes={runnerTypes}
          models={models}
          onSave={saveAgent}
          onClose={() => setEditAgent(undefined)}
        />
      )}

      {runLog && (
        <Modal
          open
          onClose={() => setRunLog(null)}
          title={`Диагностика run (pid ${runLog.run.pid || '—'})`}
          actions={<Button variant="text" onClick={() => setRunLog(null)}>Закрыть</Button>}
        >
          {/* Диалог — лист лестницы: ничего внутри не пиннит свой тон, поэтому
              поверхностей здесь нет. Моноширинной log-поверхности в библиотеке нет
              (docs/IQ_UI_CHAT_GAPS.md §10.8) — до неё каждая строка лога это
              отдельный Text: перенос строк живёт в разметке, а не в CSS. */}
          <Stack gap={200}>
            <LogBlock label="Result" text={JSON.stringify(runLog.result, null, 2)} />
            <LogBlock label="stderr" text={runLog.stderr} />
            <LogBlock label="worker log" text={runLog.workerLog} />
          </Stack>
        </Modal>
      )}

      {ctxForm && (
        <Modal
          open
          onClose={() => setCtxForm(null)}
          title="Новый execution context"
          actions={(
            <>
              <Button variant="text" onClick={() => setCtxForm(null)}>Отмена</Button>
              <Button onClick={async () => {
                try {
                  const cfg = ctxForm.type === 'git_repository'
                    ? { repoPath: ctxForm.path, ...(ctxForm.baseBranch ? { baseBranch: ctxForm.baseBranch } : {}) }
                    : { path: ctxForm.path };
                  await api.adminCreateContext({ type: ctxForm.type, name: ctxForm.name, config: cfg });
                  setCtxForm(null);
                  reload();
                } catch (e) { fail(e); }
              }}>Создать</Button>
            </>
          )}
        >
          <Stack gap={200}>
            <Select label="Тип" value={ctxForm.type}
              onChange={v => setCtxForm(f => ({ ...f, type: v }))}
              options={['none', 'local_directory', 'git_repository', 'local_server']} />
            <Field label="Название" value={ctxForm.name} onChange={v => setCtxForm(f => ({ ...f, name: v }))} />
            {/* Поля не появляются и не исчезают вслед за типом, а гаснут: диалог
                центрируется по своему содержимому, и каждая смена высоты — это
                прыжок всего окна. Постоянный состав полей = неподвижный диалог. */}
            <Field
              label={ctxForm.type === 'git_repository' ? 'Репозиторий' : 'Каталог'}
              value={ctxForm.path}
              onChange={v => setCtxForm(f => ({ ...f, path: v }))}
              disabled={ctxForm.type === 'none'}
            />
            <Field
              label="Base branch"
              value={ctxForm.baseBranch}
              onChange={v => setCtxForm(f => ({ ...f, baseBranch: v }))}
              disabled={ctxForm.type !== 'git_repository'}
            />
          </Stack>
        </Modal>
      )}
    </Stack>
  );
}
